import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import LandingDarkScope from "@/components/LandingDarkScope";
import MarketingSiteChrome from "@/components/MarketingSiteChrome";
import { API_BASE } from "@/lib/utils";

const INTENTS = {
  demo: {
    title: "Book a demo",
    subtitle: "See how Aivory EMS runs registration, onsite operations, and reporting for enterprise events.",
    source: "demo",
    submitLabel: "Request demo",
  },
  sales: {
    title: "Talk to sales",
    subtitle: "Discuss packages, deployment timelines, and enterprise requirements with our team.",
    source: "sales",
    submitLabel: "Contact sales",
  },
};

export default function ContactPage() {
  const [params] = useSearchParams();
  const intentKey = params.get("intent") === "sales" ? "sales" : "demo";
  const intent = INTENTS[intentKey];

  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    phone: "",
    eventScale: "",
    message: "",
  });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const notesPreview = useMemo(() => {
    const bits = [
      form.message.trim(),
      form.phone.trim() ? `Phone: ${form.phone.trim()}` : "",
      form.eventScale.trim() ? `Event scale: ${form.eventScale.trim()}` : "",
    ].filter(Boolean);
    return bits.join("\n");
  }, [form]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const res = await fetch(`${API_BASE}/api/public/inbound`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          intent: intentKey,
          email: form.email.trim(),
          name: form.name.trim(),
          company: form.company.trim(),
          phone: form.phone.trim(),
          eventScale: form.eventScale.trim(),
          message: form.message.trim(),
        }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body?.detail || `Request failed (${res.status})`);
      setDone(true);
    } catch (err) {
      setError(err?.message || "Could not submit your request.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <LandingDarkScope>
      <MarketingSiteChrome contentClassName="max-w-[640px] mx-auto px-4 md:px-8">
        <div className="flex gap-2 mb-8">
          <Link
            to="/contact?intent=demo"
            className={`px-4 py-2 text-sm rounded-full border transition-colors duration-200 cursor-pointer ${
              intentKey === "demo"
                ? "border-[var(--primary)] bg-[var(--primary)]/10 text-[var(--text-primary)]"
                : "border-[var(--border-default)] text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
            }`}
          >
            Book a demo
          </Link>
          <Link
            to="/contact?intent=sales"
            className={`px-4 py-2 text-sm rounded-full border transition-colors duration-200 cursor-pointer ${
              intentKey === "sales"
                ? "border-[var(--primary)] bg-[var(--primary)]/10 text-[var(--text-primary)]"
                : "border-[var(--border-default)] text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
            }`}
          >
            Talk to sales
          </Link>
        </div>

        {done ? (
          <div className="landing-card p-8 md:p-10 text-center">
            <CheckCircle2 size={40} className="mx-auto text-[var(--primary)]" strokeWidth={1.5} />
            <h1 className="font-heading text-3xl mt-6 font-semibold tracking-tight">Request received</h1>
            <p className="mt-3 text-[var(--text-secondary)] leading-relaxed">
              Our team will respond within one business day. For existing customers, log in to your workspace below.
            </p>
            <div className="mt-8 flex flex-wrap justify-center gap-3">
              <Link to="/signup" className="btn-primary inline-flex">
                Start 14-day Studio trial <ArrowRight size={14} />
              </Link>
              <Link to="/login" className="btn-secondary inline-flex">
                Log in
              </Link>
              <Link to="/" className="btn-ghost inline-flex border border-[var(--border-default)]">
                Back to homepage
              </Link>
            </div>
          </div>
        ) : (
          <>
            <h1 className="font-heading text-4xl md:text-5xl font-semibold tracking-tight">{intent.title}</h1>
            <p className="mt-4 text-[var(--text-secondary)] leading-relaxed">{intent.subtitle}</p>

            <form onSubmit={submit} className="landing-card p-6 md:p-8 mt-10 space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <label className="block sm:col-span-2">
                  <span className="text-xs text-[var(--text-tertiary)]">Work email</span>
                  <input
                    className="input-dark w-full mt-1"
                    type="email"
                    required
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                  />
                </label>
                <label className="block">
                  <span className="text-xs text-[var(--text-tertiary)]">Full name</span>
                  <input
                    className="input-dark w-full mt-1"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                  />
                </label>
                <label className="block">
                  <span className="text-xs text-[var(--text-tertiary)]">Company</span>
                  <input
                    className="input-dark w-full mt-1"
                    value={form.company}
                    onChange={(e) => setForm({ ...form, company: e.target.value })}
                  />
                </label>
                <label className="block">
                  <span className="text-xs text-[var(--text-tertiary)]">Phone</span>
                  <input
                    className="input-dark w-full mt-1"
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  />
                </label>
                <label className="block">
                  <span className="text-xs text-[var(--text-tertiary)]">Expected event scale</span>
                  <input
                    className="input-dark w-full mt-1"
                    placeholder="e.g. 2,000 attendees"
                    value={form.eventScale}
                    onChange={(e) => setForm({ ...form, eventScale: e.target.value })}
                  />
                </label>
                <label className="block sm:col-span-2">
                  <span className="text-xs text-[var(--text-tertiary)]">How can we help?</span>
                  <textarea
                    className="input-dark w-full mt-1 min-h-[100px]"
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                  />
                </label>
              </div>
              {error ? <p className="text-sm text-[#ef4444]">{error}</p> : null}
              <button type="submit" className="btn-primary w-full justify-center" disabled={busy}>
                {busy ? "Submitting…" : intent.submitLabel}
              </button>
              {notesPreview ? (
                <p className="text-[10px] font-mono text-[var(--text-tertiary)] hidden" aria-hidden>
                  {notesPreview}
                </p>
              ) : null}
            </form>

            <p className="mt-6 text-sm text-[var(--text-tertiary)] text-center">
              Already using Aivory EMS?{" "}
              <Link to="/login" className="text-[var(--primary)] hover:underline">
                Log in to your workspace
              </Link>
            </p>
          </>
        )}
      </MarketingSiteChrome>
    </LandingDarkScope>
  );
}
